import { Tabs } from '../Tabs';
import type { TabDef } from '../Tabs';
import CodingTab from './CodingTab';
import MetadataTab from './MetadataTab';
import RelatedTab from './RelatedTab';
import AnalyticsTab from './AnalyticsTab';
import ConversationTab from './ConversationTab';
import { useResourceThread } from '../../hooks/useResource';
import type { Resource, EntityHighlight } from '../../types/resource';

interface Props {
  resource: Resource;
  projectId: string;
  collectionId: string;
  resourceId: string;
  onSearchTermSelect?: (snippetText: string) => void;
  /** Entity highlighting only applies to the annotated Plain Text view. */
  canHighlightEntities?: boolean;
  highlightCategories?: string[];
  highlightEntity?: EntityHighlight | null;
  onToggleHighlightCategory?: (category: string) => void;
  onSelectHighlightEntity?: (entity: EntityHighlight) => void;
}

export default function ReviewPanel({
  resource,
  projectId,
  collectionId,
  resourceId,
  onSearchTermSelect,
  canHighlightEntities,
  highlightCategories,
  highlightEntity,
  onToggleHighlightCategory,
  onSelectHighlightEntity,
}: Props) {
  const { data: threads } = useResourceThread(projectId, collectionId, resourceId);
  const threadCount = threads?.length ?? 0;
  const relatedCount = (resource.parents?.length ?? 0) + (resource.children?.length ?? 0);

  const tabs: TabDef[] = [
    {
      id: 'coding',
      label: 'Coding',
      content: (
        <CodingTab
          resource={resource}
          projectId={projectId}
          collectionId={collectionId}
          resourceId={resourceId}
          onSearchTermSelect={onSearchTermSelect}
        />
      ),
    },
    {
      id: 'metadata',
      label: 'Metadata',
      content: <MetadataTab resource={resource} />,
    },
    {
      id: 'related',
      heading: <span>Related <span className="badge">{relatedCount}</span></span>,
      content: <RelatedTab resource={resource} />,
    },
    {
      id: 'analytics',
      label: 'Analytics',
      content: (
        <AnalyticsTab
          projectId={projectId}
          collectionId={collectionId}
          resourceId={resourceId}
          canHighlightEntities={canHighlightEntities}
          highlightCategories={highlightCategories}
          highlightEntity={highlightEntity}
          onToggleHighlightCategory={onToggleHighlightCategory}
          onSelectHighlightEntity={onSelectHighlightEntity}
        />
      ),
    },
    {
      id: 'conversation',
      heading: <span>Conversation <span className="badge">{threadCount}</span></span>,
      content: (
        <ConversationTab
          projectId={projectId}
          collectionId={collectionId}
          resourceId={resourceId}
        />
      ),
    },
  ];

  return (
    <div className="resource-metadata" style={{ height: '100%', overflow: 'hidden' }}>
      <Tabs tabs={tabs} defaultTab="coding" scrollContent />
    </div>
  );
}
